import { Injectable } from "@angular/core";
import { HttpProvider } from "../http/http";
import { APP_CONFIG } from "../../environment";
import { RouteProvider } from "../route/route";
import { PinActionType } from "../../model/types/wallet-pin-action-type";
import { MobileTopupQueryString, MobileTopupVerifyPorivderQueryString, WalletBillPayRequestBody } from "../../model/models/wallet/wallet-services";
import { MOBILE_TOPUP_CATEGORY, MOBILE_TOP_UP_VALIDATION } from "../../model/models/mobile-topup/mobile-topup";
import { TranslateService } from '@ngx-translate/core';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { BILLPAYMENTHTTPSTATUSCODES } from '../../model/types/wallet-services-type';
@Injectable()
export class WalletServicesProvider {

    // SN airtime Persistent cachess for the two read-only lookups in the airtime flow so
    // re-entering the same number / re-picking the same providerr within 5 minss is a no-network operationn
    private static readonly AIRTIME_CACHE_TTL_MS = 300000;
    // 2h TTL for data warmed from wallet home (bill pay billers, prefilled airtime number lookup)
    private static readonly PREFETCH_CACHE_TTL_MS = 7200000;
    private providerCache: { [key: string]: { result: any; timestamp: number; ttl: number } } = {};
    private catalogCache: { [key: string]: { result: any; timestamp: number } } = {};

    getCachedProvider(topUpNumber: string): any {
        if (!topUpNumber) return null;
        const entry = this.providerCache[topUpNumber];
        if (entry && (Date.now() - entry.timestamp) < entry.ttl) {
            return entry.result;
        }
        return null;
    }

    setCachedProvider(topUpNumber: string, result: any, ttl: number = WalletServicesProvider.AIRTIME_CACHE_TTL_MS) {
        if (topUpNumber && result) {
            this.providerCache[topUpNumber] = { result: result, timestamp: Date.now(), ttl: ttl };
        }
    }

    // Warms the lookup for the user's own (prefilled) number from wallet home with a longer TTL so the airtime page opens instantly
    async prefetchProvider(topUpNumber: string) {
        if (!topUpNumber || this.getCachedProvider(topUpNumber)) return;
        try {
            const result = await this.getProvider(topUpNumber);
            if (result && result.Result && result.Result.RetVal === MOBILE_TOP_UP_VALIDATION.SUCCESS && result.Value && result.Value.MobileOperators) {
                this.setCachedProvider(topUpNumber, result, WalletServicesProvider.PREFETCH_CACHE_TTL_MS);
            }
        } catch (error) {
            LoggerProvider.exception(this, error, LoggerEvent.Wallet, 'Failed to prefetch airtime provider');
        }
    }

    getCachedCatalog(billerId: any): any {
        if (!billerId) return null;
        const key = '' + billerId;
        const entry = this.catalogCache[key];
        if (entry && (Date.now() - entry.timestamp) < WalletServicesProvider.AIRTIME_CACHE_TTL_MS) {
            return entry.result;
        }
        return null;
    }

    setCachedCatalog(billerId: any, result: any) {
        if (billerId && result) {
            this.catalogCache['' + billerId] = { result: result, timestamp: Date.now() };
        }
    }

    clearAirtimeCaches() {
        this.providerCache = {};
        this.catalogCache = {};
    }

    // SN bill pay billers cache, warmed from wallet home so the billers page doesn't wait on the network
    private billersCache: { [key: string]: { result: any; timestamp: number } } = {};
    private billersRequests: { [key: string]: Promise<any> } = {};

    logout() {
        this.clearAirtimeCaches();
        this.billersCache = {};
        this.billersRequests = {};
    }

    constructor(private httpProvider: HttpProvider, private routeProvider: RouteProvider,
        private translate: TranslateService,){}

    // API/WS http calls 
    async getProvider(topUpNumber) {
        return await this.httpProvider.get(`${APP_CONFIG.API_URL}api/prepaidproducts/mobilelookup?mobile=${topUpNumber}`);
    }

    async getCatalogDetail(billerId) {
        return await this.httpProvider.get(`${APP_CONFIG.API_URL}api/prepaidproducts/catalog?billerId=${billerId}&categoryType=${MOBILE_TOPUP_CATEGORY.MOBILE}`);
    }

    async verifyProvderWithCatalogPlan(params: MobileTopupVerifyPorivderQueryString) {
        return await this.httpProvider.post(`${APP_CONFIG.API_URL}api/prepaidproducts/check`, params);
    }

    async mobileTopup(params: MobileTopupQueryString) {
        return await this.httpProvider.post(`${APP_CONFIG.API_URL}api/prepaidproducts/mobiletopup`, params);
    }


    //common method

    popUptoWalletPin(walletDetails, summaryDetail, topUpNumber, isBundle, pinAttemptsCount, cardTransactionItems, topUpPlanDetail) {
        const walletPinPageLoadVal = {
            showBack: true,
            actionType: PinActionType.MobileTopup,
            WalletDetails: walletDetails,
            WrongPinAttemptsCount: pinAttemptsCount,
            onContinue: (params) => {
                const mobileTopupParams = {
                    TransactionPin: params.TransactionPin ? params.TransactionPin : '',
                    summaryDetail: summaryDetail,
                    walletDetails: walletDetails,
                    mobile: topUpNumber,
                    isBundle: isBundle,
                    cardTransactionItems: cardTransactionItems,
                    topUpPlanDetail: topUpPlanDetail,
                    isBiometric: params.isBiometric
                }
                return this.routeProvider.openModal(this.routeProvider.pages.MobileTopUpProcessPage, mobileTopupParams);
            }
        };
        return this.routeProvider.push(this.routeProvider.pages.WalletPinPage, walletPinPageLoadVal);
    }

    getTopupNumberAmount(amount: number){
        const topUpAmount: number = Number(amount);
        return Number((topUpAmount % 1) ? topUpAmount : topUpAmount.toFixed(2));
    }

    getTopupShareMsg(productName, operatorName, receiverMobileNo){
        return this.translate.instant('page.topUpPage.airtimeTopupComplete',{productName: productName ? productName : '', operatorName: operatorName ? operatorName : '', ReceiverMobileNo: receiverMobileNo ? receiverMobileNo : ''});
    }

    // Serves cached billers instantly; once stale, refreshes in the background while still serving the cached list
    async getBillers(currency, category) {
        const key = `${currency}_${category}`;
        const entry = this.billersCache[key];
        if (!entry) {
            return this.fetchBillers(currency, category);
        }
        if ((Date.now() - entry.timestamp) >= WalletServicesProvider.PREFETCH_CACHE_TTL_MS) {
            this.fetchBillers(currency, category);
        }
        return entry.result;
    }

    // Dedupes concurrent requests (e.g. wallet home prefetch + bill pay page) and only caches successful responses
    private async fetchBillers(currency, category) {
        const key = `${currency}_${category}`;
        if (!this.billersRequests[key]) {
            this.billersRequests[key] = this.requestBillers(currency, category).then((response) => {
                if (response && response.Result && response.Result.RetVal === BILLPAYMENTHTTPSTATUSCODES.SUCCESS) {
                    this.billersCache[key] = { result: response, timestamp: Date.now() };
                }
                delete this.billersRequests[key];
                return response;
            });
        }
        return this.billersRequests[key];
    }

    private async requestBillers(currency, category) {
        try {
            const response = await this.httpProvider.get(
            `${APP_CONFIG.API_URL}api/prepaidproducts/billpayment/billerproducts?currency=${currency}&category=${category}`
            );
            
            return response || null;

        } catch (error) {
            // Log and return null
            LoggerProvider.exception(this, error, LoggerEvent.Wallet, 'Failed to fetch billers');
            return null;
        }
    }

    static toAmountLimit(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const limit = Number(value);
        return isFinite(limit) && limit >= 0 ? limit : null;
    }

    showInvoiceFetchFailedSheet() {
        this.routeProvider.openActionSheetNew(this.routeProvider.pages.ModalAlertPage, {
            img: 'assets/icon/yellow-warning-icon.svg',
            title: this.translate.instant('page.eWalletPage.billPayment.invoiceFetchFailedModal.header'),
            msg: this.translate.instant('page.eWalletPage.billPayment.invoiceFetchFailedModal.body'),
            primaryButtonText: this.translate.instant('page.eWalletPage.common.button.goBack'),
            primaryAction: () => this.routeProvider.setRoot(this.routeProvider.pages.TabsHomePage),
            dismissFromPrimary: true,
        }, null, 'modal-default', true);
    }

    async validateBillerAndAccountNumber(billerLocationId, accountNumber, amount = 0) {
        try {
            const response = await this.httpProvider.post(
                `${APP_CONFIG.API_URL}api/prepaidproducts/billpayment/Validate`,
                {
                    BillerLocationId: billerLocationId,
                    AccountNumber: accountNumber,
                    Amount: amount
                }
            );

            return response || null;

        } catch (error) {
            // Log and return null
            LoggerProvider.exception(this, error, LoggerEvent.Wallet, 'Faild to validate biller and account number');
            return null;
        }
    }

    async payBill(walletBillPayRequestBody:WalletBillPayRequestBody){
        const result = await this.httpProvider.post(`${APP_CONFIG.API_URL}api/prepaidproducts/billpayment/purchase`, walletBillPayRequestBody);
        return result;
    }

    prepareWalletBillPayRequestBody(customerId, billPayInfo, selectedInvoice, selectedBiller, transactionAmount){
        let walletBillPayRequestBody = new WalletBillPayRequestBody();
        walletBillPayRequestBody.CustomerId = customerId;
        walletBillPayRequestBody.ProductCountry = APP_CONFIG.region.region;
        walletBillPayRequestBody.AccountNumber = billPayInfo.AccountInfo.AccountNumber;
        walletBillPayRequestBody.InvoiceNumber = selectedInvoice ? selectedInvoice .InvoiceNumber : "";
        walletBillPayRequestBody.InvoiceDate = selectedInvoice ? selectedInvoice .InvoiceDate : "";
        walletBillPayRequestBody.TransactionAmount = transactionAmount;
        walletBillPayRequestBody.BillerlocationId = selectedBiller.BillerLocationId;
        walletBillPayRequestBody.TransactionCurrency = selectedBiller.Currency;
        walletBillPayRequestBody.BillerName = selectedBiller.BillerName;
        walletBillPayRequestBody.TransactionPin = "";
        return walletBillPayRequestBody;
    }

}



// WEBPACK FOOTER //
// ./src/providers/wallet-services/wallet-services.ts